// Merkan · Cloudflare Turnstile
//
// Supabase Auth prüft im Projekt merkan-prod jede Anmeldung, jeden Anmeldelink und jede Passwort-Zurücksetzung
// mit Turnstile (Authentication > Attack Protection). Der Schutz gilt damit für alle Mandanten gleichermaßen.
// Jeder Aufruf von signInWithPassword, signInWithOtp und resetPasswordForEmail im Browser braucht deshalb
// options.captchaToken. Ein Token gilt genau einmal: nach jedem Versuch neu anfordern (zuruecksetzen()).
//
// Widget im Cloudflare-Konto: "Merkan Anmeldung", Hostnamen parlinex.io (alle Subdomains) und merkan-admin.pages.dev.
// Der Sitekey ist öffentlich. Für eine andere Umgebung kann er über VITE_TURNSTILE_SITEKEY überschrieben werden.
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

const STANDARD_SITEKEY = '0x4AAAAAAFwRuU9_Cqb6XkL4';
export const TURNSTILE_SITEKEY = (import.meta.env.VITE_TURNSTILE_SITEKEY as string | undefined) || STANDARD_SITEKEY;

const SKRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render: (ziel: HTMLElement, optionen: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
}
declare global {
  interface Window { turnstile?: TurnstileApi }
}

let laden: Promise<TurnstileApi> | null = null;

function skriptLaden(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (laden) return laden;
  laden = new Promise<TurnstileApi>((ok, fehler) => {
    const s = document.createElement('script');
    s.src = SKRIPT;
    s.async = true;
    s.defer = true;
    s.onload = () => (window.turnstile ? ok(window.turnstile) : fehler(new Error('Turnstile nicht verfügbar')));
    s.onerror = () => {
      laden = null;
      fehler(new Error('Turnstile konnte nicht geladen werden'));
    };
    document.head.appendChild(s);
  });
  return laden;
}

export interface TurnstileRef {
  /** Neues Token anfordern, nach jedem Anmeldeversuch aufrufen */
  zuruecksetzen: () => void;
}

interface Props {
  /** Token liegt vor (null, wenn abgelaufen oder zurückgesetzt) */
  onToken: (token: string | null) => void;
  /** Turnstile meldet einen Fehler, z. B. Netzwerk oder unbekannter Hostname */
  onFehler?: (text: string) => void;
  /** Aktion für die Auswertung im Cloudflare-Dashboard, z. B. anmeldung, portal, admin */
  aktion?: string;
  className?: string;
}

/** Turnstile-Widget, Darstellung "flexible", passt sich der Breite der Karte an */
export const Turnstile = forwardRef<TurnstileRef, Props>(function Turnstile({ onToken, onFehler, aktion, className }, ref) {
  const ziel = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const tokenRef = useRef(onToken);
  const fehlerRef = useRef(onFehler);
  tokenRef.current = onToken;
  fehlerRef.current = onFehler;

  useImperativeHandle(ref, () => ({
    zuruecksetzen: () => {
      tokenRef.current(null);
      if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current);
    },
  }), []);

  useEffect(() => {
    let aktiv = true;
    skriptLaden()
      .then((api) => {
        if (!aktiv || !ziel.current || widgetId.current) return;
        widgetId.current = api.render(ziel.current, {
          sitekey: TURNSTILE_SITEKEY,
          action: aktion,
          language: 'de',
          theme: 'auto',
          size: 'flexible',
          appearance: 'interaction-only',
          'refresh-expired': 'auto',
          callback: (t: string) => tokenRef.current(t),
          'expired-callback': () => tokenRef.current(null),
          'timeout-callback': () => tokenRef.current(null),
          'error-callback': () => {
            tokenRef.current(null);
            fehlerRef.current?.('Die Sicherheitsprüfung ist fehlgeschlagen. Bitte die Seite neu laden.');
            return true;
          },
        });
      })
      .catch(() => fehlerRef.current?.('Die Sicherheitsprüfung konnte nicht geladen werden. Bitte Werbeblocker prüfen und die Seite neu laden.'));
    return () => {
      aktiv = false;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [aktion]);

  return <div ref={ziel} className={className} style={{ minHeight: 0 }} />;
});
